import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Spin } from 'antd';
import { SplitShell } from '../layout/split/SplitShell';

/**
 * /go/:token — I-10 SMS entry point. Hits the server which single-uses the
 * token and returns the target path. We navigate there.
 *
 * Server also serves a native 302 when the client sends `Accept: text/html`
 * — so pasting the URL into a fresh tab works even without JS. This page
 * is here for the SPA-in-tab case where an anchor click routes through
 * react-router.
 */
export function GoPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    fetch(`/api/accounts/go/${encodeURIComponent(token)}`, {
      headers: { accept: 'application/json' },
      credentials: 'include',
    }).then(async (r) => {
      if (cancelled) return;
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        setErr((body as { error?: string }).error === 'link_not_found'
          ? "That link has expired or doesn't exist."
          : `Couldn't open the link (${r.status}).`);
        return;
      }
      const { target_path } = await r.json() as { target_path: string };
      window.location.href = target_path;
    }).catch((e) => {
      if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
    });
    return () => { cancelled = true; };
  }, [token]);

  return (
    <SplitShell>
      <div style={{ padding: 24 }}>
        {err ? (
          <Alert type="warning" showIcon message="Couldn't open link" description={err} />
        ) : (
          <div style={{ textAlign: 'center' }}>
            <Spin />
            <div style={{ marginTop: 12, color: '#5b6478', fontSize: 13 }}>Taking you there…</div>
          </div>
        )}
      </div>
    </SplitShell>
  );
}
